import { useMemo, useState } from "react";
import PageHero from "@/components/feature/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import Pagination from "@/components/base/Pagination";
import NewsCard from "@/components/feature/NewsCard";
import useSeo from "@/hooks/useSeo";
import { news, newsCategories } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function News() {
  useSeo({
    title: "激情丛林电影资讯 - 幕后花絮与官方动态",
    description:
      "《激情丛林》最新资讯动态：幕后拍摄花絮、主创活动报道与官方公告，按栏目筛选与时间排序，第一时间了解影片相关新闻。",
    keywords: "激情丛林资讯,电影资讯,幕后花絮,活动报道,官方公告",
  });

  const [category, setCategory] = useState("全部");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = news.filter((n) => category === "全部" || n.category === category);
    return [...list].sort((a, b) => (order === "desc" ? (a.date < b.date ? 1 : -1) : a.date > b.date ? 1 : -1));
  }, [category, order]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetAll = () => {
    setCategory("全部");
    setOrder("desc");
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHero
        title="资讯动态"
        subtitle="从幕后拍摄的琐碎日常，到影片相关的官方公告，这里记录着《激情丛林》延续至今的每一次回响。"
        crumbs={[{ label: "资讯" }]}
      />

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <SectionHeading
          eyebrow="News"
          title="全部资讯"
          desc={`当前共收录 ${filtered.length} 条资讯`}
        />

        <div className="panel mb-8 flex flex-col gap-4 rounded-lg p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div className="flex flex-wrap items-center gap-2">
            {newsCategories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setCategory(c);
                  setPage(1);
                }}
                className={`whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors ${
                  category === c
                    ? "bg-primary-500 font-semibold text-background-50"
                    : "border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-accent-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 font-label text-xs text-foreground-500">
              <i className="ri-sort-desc"></i>
              时间排序
            </span>
            <select
              value={order}
              onChange={(e) => {
                setOrder(e.target.value);
                setPage(1);
              }}
              aria-label="时间排序"
              className="cursor-pointer rounded-md border border-background-700 bg-background-900 px-3 py-2 font-label text-sm text-foreground-100 focus:border-primary-500 focus:outline-none"
            >
              <option value="desc">新 → 旧</option>
              <option value="asc">旧 → 新</option>
            </select>
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {pageItems.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination current={safePage} total={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="panel flex flex-col items-center justify-center rounded-lg px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-400">
              <i className="ri-newspaper-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-bold text-foreground-50">
              该栏目暂无内容
            </h3>
            <p className="mt-2 max-w-md text-sm text-foreground-400">
              换一个栏目看看，或重置筛选浏览全部资讯。
            </p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </section>
    </div>
  );
}